import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import * as PIXI from 'pixi.js';
import { SerializedWorld } from '../types/world';

interface PixiRendererProps {
  world: SerializedWorld;
}

const PixiRenderer: React.FC<PixiRendererProps> = ({ world }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const appRef = useRef<PIXI.Application | null>(null);
  const isMountedRef = useRef(true);
  const cleanedUpRef = useRef(false);

  const render = useCallback(() => {
    if (!appRef.current || !appRef.current.stage) return;

    appRef.current.stage.removeChildren();

    const tileSize = 20;
    const graphics = new PIXI.Graphics();

    // Render terrain
    console.log('World grid dimensions:', world.grid.width, 'x', world.grid.height);

    for (let y = 0; y < world.grid.height; y++) {
      for (let x = 0; x < world.grid.width; x++) {
        const terrain = world.grid.tiles[y][x];
        graphics.beginFill(terrain.color);
        graphics.drawRect(x * tileSize, y * tileSize, tileSize, tileSize);
        graphics.endFill();
      }
    }

    appRef.current.stage.addChild(graphics);

    // Render entities
    console.log('Number of entities:', world.entities.length);
    world.entities.forEach((entity) => {
      const position = entity.components.position;
      const renderable = entity.components.renderable;

      if (position && renderable) {
        const text = new PIXI.Text(renderable.symbol, {
          fontFamily: 'Arial',
          fontSize: 16,
          fill: 0xffffff,
        });
        text.x = position.x * tileSize;
        text.y = position.y * tileSize;
        appRef.current?.stage.addChild(text);
      }
    });

    console.log('Render completed');
  }, [world]);

  const pixiOptions = useMemo(() => ({
    backgroundColor: 0x1099bb,
    resizeTo: window, // This will make the canvas resize with the window
    autoDensity: true, // This ensures that the renderer respects the device's pixel ratio
  }), []);

  const handleResize = useCallback(() => {
    if (appRef.current && canvasRef.current && appRef.current.renderer) {
      appRef.current.renderer.resize(canvasRef.current.clientWidth, canvasRef.current.clientHeight);
      render();
    }
  }, [render]);

  useEffect(() => {
    if (!canvasRef.current) {
      console.log('Canvas ref is null');
      return;
    }

    console.log('Creating PIXI Application');
    let pixiApp: PIXI.Application | null = null;
    const canvas = canvasRef.current;

    try {
      pixiApp = new PIXI.Application({
        view: canvas,
        ...pixiOptions,
      });

      appRef.current = pixiApp;
      console.log('PIXI Application created successfully');

      // Wait for the next frame to ensure the application is fully initialized
      requestAnimationFrame(() => {
        window.addEventListener('resize', handleResize);
        handleResize(); // Initial resize
        render();
        console.log('Initial render completed');
      });
    } catch (error) {
      console.error('Error creating PIXI Application:', error);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      console.log('Cleanup function called');
      isMountedRef.current = false;
      if (pixiApp && !cleanedUpRef.current) {
        cleanedUpRef.current = true;
        requestAnimationFrame(() => {
          try {
            console.log('Cleanup started');
            
            // Safely remove all children from the stage
            if (pixiApp?.stage) {
              while (pixiApp.stage.children.length > 0) {
                const child = pixiApp.stage.removeChildAt(0);
                if (child && typeof (child as any).destroy === 'function') {
                  if ('children' in child) {
                    (child as PIXI.Container).destroy({ children: true });
                  } else {
                    (child as any).destroy();
                  }
                }
              }
            }
            console.log('Children removed');

            // Destroy the renderer
            pixiApp?.renderer?.destroy(true);
            console.log('Renderer destroyed');

            // Clear the ticker
            pixiApp?.ticker?.destroy();
            console.log('Ticker destroyed');

            // Remove the canvas from the DOM
            if (canvas && canvas.parentNode) {
              canvas.parentNode.removeChild(canvas);
              console.log('Canvas removed from DOM');
            }

            // Clear all references
            if (pixiApp) {
              (pixiApp.stage as any) = null;
              (pixiApp.renderer as any) = null;
              (pixiApp.ticker as any) = null;
            }

            console.log('PIXI Application resources cleaned up');
          } catch (error) {
            console.error('Error during cleanup:', error);
          } finally {
            appRef.current = null;
          }
        });
      }
    };
  }, [pixiOptions, render, handleResize]);

  useEffect(() => {
    if (appRef.current) {
      console.log('Rendering due to world update');
      render();
    }
  }, [render]);

  console.log('PixiRenderer rendering');
  return (
    <div style={{ width: '100%', height: '100%', position: 'absolute' }}>
      <canvas ref={canvasRef} style={{ display: 'block' }} />
    </div>
  );
};

export default PixiRenderer;